import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ApiSdkService } from 'src/app/services/api-sdk.service';
import { CreditsPackI } from 'src/app/interfaces/credits.interface';

interface FieldError {
  key: string;
  params: { min?: number; max?: number };
}

@Component({
  selector: 'app-auto-recharge-modal',
  templateUrl: './auto-recharge-modal.component.html',
  styleUrls: ['./auto-recharge-modal.component.scss']
})
export class AutoRechargeModalComponent implements OnInit {
  loading = true;
  saving = false;
  error: string | null = null;

  // Per-field bounds (provided by backend, with safe defaults)
  thresholdMin = 50;
  thresholdMax = 10000;
  amountMin = 200;
  amountMax = 20000;

  // Form model
  enabled = false;
  thresholdCredits = 100;
  rechargeCredits = 2000;

  private packs: CreditsPackI[] = [];

  constructor(
    private modalCtrl: ModalController,
    private apiSdkService: ApiSdkService
  ) {}

  async ngOnInit() {
    await this.load();
  }

  async load() {
    this.loading = true;
    this.error = null;
    try {
      const [configRes, packsRes] = await Promise.all([
        this.apiSdkService.getAutoRechargeConfig(),
        this.apiSdkService.getCreditsPacks(),
      ]);

      const data = configRes?.success?.data;
      if (data) {
        this.thresholdMin = data.thresholdMin ?? this.thresholdMin;
        this.thresholdMax = data.thresholdMax ?? this.thresholdMax;
        this.amountMin = data.amountMin ?? this.amountMin;
        this.amountMax = data.amountMax ?? this.amountMax;
        if (data.config) {
          this.enabled = !!data.config.enabled;
          this.thresholdCredits = data.config.thresholdCredits ?? this.thresholdCredits;
          this.rechargeCredits = data.config.rechargeCredits ?? this.rechargeCredits;
        }
      }

      if (packsRes?.success?.data) {
        this.packs = packsRes.success.data;
      }
    } catch (err) {
      console.error('Error loading auto-recharge config:', err);
      this.error = 'LOAD_ERROR';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Approximate euro price for a credit amount, using the same tiered (volume-discount) rate
   * as the credit packs so it matches what the backend actually charges.
   */
  approxPrice(credits: number): number {
    const sorted = this.packs.filter(p => p.quantity > 0).sort((a, b) => a.quantity - b.quantity);
    if (sorted.length === 0) return parseFloat((credits * 0.1).toFixed(2));
    let rate = sorted[0].price / sorted[0].quantity;
    for (const p of sorted) {
      if (p.quantity <= credits) rate = p.price / p.quantity;
    }
    return parseFloat((credits * rate).toFixed(2));
  }

  get rechargeApprox(): string {
    const value = Number(this.rechargeCredits) || 0;
    return this.approxPrice(value).toFixed(2);
  }

  /** Validate a field against its bounds, returning a translatable error or null. */
  private fieldError(value: number, min: number, max: number): FieldError | null {
    const n = Number(value);
    if (!Number.isFinite(n) || value === null || value === undefined) {
      return { key: 'AUTO_RECHARGE_MIN_ERROR', params: { min } };
    }
    if (!Number.isInteger(n)) return { key: 'AUTO_RECHARGE_DECIMAL_ERROR', params: {} };
    if (n < min) return { key: 'AUTO_RECHARGE_MIN_ERROR', params: { min } };
    if (n > max) return { key: 'AUTO_RECHARGE_MAX_ERROR', params: { max } };
    return null;
  }

  get thresholdError(): FieldError | null {
    return this.fieldError(this.thresholdCredits, this.thresholdMin, this.thresholdMax);
  }

  get rechargeError(): FieldError | null {
    return this.fieldError(this.rechargeCredits, this.amountMin, this.amountMax);
  }

  get isValid(): boolean {
    return !this.thresholdError && !this.rechargeError;
  }

  async save() {
    // Block saving with an invalid config (inline errors are already shown).
    if (this.enabled && !this.isValid) {
      return;
    }

    this.saving = true;
    this.error = null;
    try {
      const result = await this.apiSdkService.updateAutoRechargeConfig({
        enabled: this.enabled,
        thresholdCredits: Math.round(Number(this.thresholdCredits)),
        rechargeCredits: Math.round(Number(this.rechargeCredits)),
      });

      if (result?.success) {
        await this.modalCtrl.dismiss({ saved: true });
      } else {
        this.error = result?.error?.error?.message || 'SAVE_ERROR';
      }
    } catch (err) {
      console.error('Error saving auto-recharge config:', err);
      this.error = 'SAVE_ERROR';
    } finally {
      this.saving = false;
    }
  }

  async close() {
    await this.modalCtrl.dismiss();
  }
}
